
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingIndicator from '../Common/LoadingIndicator';
import './ModalBlock.css';
import {
    createLessonPageBlock, updateLessonPageBlock,
    getQuizBlocks,
    getQuizQuestions,
    getQuizChoices
} from '../../api/lessons';

function ModalBlock({
    type,
    Action,
    selectedBlock,
    lessonPage,
    closeModal,
    modalHeading,
    setModalHeading,
    modalHeadingSize,
    setModalHeadingSize,
    modalBody,
    setModalBody,
    modalBodySize,
    setModalBodySize,
    saveContent,
    onSaveQuizDraft,
    onModalSave,
    onLogChange,
}) {
    const { t } = useTranslation();

    // Quiz Builder State (multiple questions)
    const [quizQuestions, setQuizQuestions] = useState([]);
    const [loadingQuiz, setLoadingQuiz] = useState(false);
    const [mediaMode, setMediaMode] = useState('file');
    const [mediaUrlInput, setMediaUrlInput] = useState('');
    const [mediaFile, setMediaFile] = useState(null);
    const [mediaPreview, setMediaPreview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toInteger = (value, fallback = 0) => {
        const parsedValue = Number.parseInt(value, 10);
        return Number.isFinite(parsedValue) ? parsedValue : fallback;
    };

    // Fetch quiz block/questions/choices for Update
    useEffect(() => {
        async function fetchQuizBlockData() {
            if (type === 'quiz' && Action === 'Update' && selectedBlock) {
                setLoadingQuiz(true);
                try {
                    // Fetch all quiz blocks and filter for this lesson-page-block
                    const allQuizBlocks = await getQuizBlocks();
                    const quizBlock = allQuizBlocks.find(b => b.lesson_page_block === selectedBlock.lesson_page_block || b.lesson_page_block === selectedBlock.id);
                    if (!quizBlock || !quizBlock.id) {
                        setQuizQuestions([]);
                        setLoadingQuiz(false);
                        return;
                    }
                    // Fetch questions for this quiz block
                    const questions = await getQuizQuestions(quizBlock.id);
                    // For each question, fetch choices
                    const questionsWithChoices = await Promise.all(
                        questions.map(async (q) => {
                            const allOptions = await getQuizChoices(q.id);
                            // Filter to only include choices for THIS specific question
                            const options = Array.isArray(allOptions) 
                                ? allOptions.filter(opt => opt.quiz_question === q.id)
                                : [];
                            return {
                                text: q.text || '',
                                type: q.type || quizBlock.choice_type || 'single',
                                options: options.length > 0
                                    ? options.map(opt => ({ text: opt.text, isCorrect: !!opt.is_correct }))
                                    : [ { text: '', isCorrect: false }, { text: '', isCorrect: false } ]
                            };
                        })
                    );
                    setQuizQuestions(questionsWithChoices.length > 0 ? questionsWithChoices : [
                        {
                            text: '',
                            type: 'single',
                            options: [ { text: '', isCorrect: false }, { text: '', isCorrect: false } ]
                        }
                    ]);
                } catch (err) {
                    setQuizQuestions([]);
                }
                setLoadingQuiz(false);
            } else if (type === 'quiz' && Action === 'Add') {
                // Default for Add
                setQuizQuestions([
                    {
                        text: '',
                        type: 'single',
                        options: [ { text: '', isCorrect: false }, { text: '', isCorrect: false } ]
                    }
                ]);
            }
        }
        fetchQuizBlockData();
        // eslint-disable-next-line
    }, [type, Action, selectedBlock]);

    useEffect(() => {
        if (type === 'image' || type === 'video') {
            const existingMediaUrl = selectedBlock?.mediaUrl || selectedBlock?.URL_media || selectedBlock?.value || '';
            setMediaMode(Action === 'Update' && existingMediaUrl ? 'url' : 'file');
            setMediaUrlInput(existingMediaUrl);
            setMediaPreview(existingMediaUrl);
            setMediaFile(null);
        }
    }, [type, Action, selectedBlock]);

    // Add, remove, update questions
    const addQuizQuestion = () => {
        setQuizQuestions(qs => [
            ...qs,
            { text: '', type: 'single', options: [ { text: '', isCorrect: false }, { text: '', isCorrect: false } ] }
        ]);
    };
    const removeQuizQuestion = (qIdx) => {
        setQuizQuestions(qs => qs.length > 1 ? qs.filter((_, i) => i !== qIdx) : qs);
    };
    const updateQuizQuestionText = (qIdx, val) => {
        setQuizQuestions(qs => qs.map((q, i) => i === qIdx ? { ...q, text: val } : q));
    };
    const updateQuizType = (qIdx, val) => {
        setQuizQuestions(qs => qs.map((q, i) => {
            if (i !== qIdx) return q;
            // If switching to single, ensure only one correct
            let newOptions = q.options;
            if (val === 'single') {
                let found = false;
                newOptions = q.options.map(opt => {
                    if (opt.isCorrect && !found) { found = true; return { ...opt, isCorrect: true }; }
                    return { ...opt, isCorrect: false };
                });
            }
            return { ...q, type: val, options: newOptions };
        }));
    };

    // Add, remove, update options
    const addQuizOption = (qIdx) => {
        setQuizQuestions(qs => qs.map((q, i) => i === qIdx ? {
            ...q,
            options: [ ...q.options, { text: '', isCorrect: false } ]
        } : q));
    };
    const removeQuizOption = (qIdx, oIdx) => {
        setQuizQuestions(qs => qs.map((q, i) => i === qIdx ? {
            ...q,
            options: q.options.length > 2 ? q.options.filter((_, j) => j !== oIdx) : q.options
        } : q));
    };
    const updateQuizOptionText = (qIdx, oIdx, val) => {
        setQuizQuestions(qs => qs.map((q, i) => i === qIdx ? {
            ...q,
            options: q.options.map((opt, j) => j === oIdx ? { ...opt, text: val } : opt)
        } : q));
    };
    const toggleQuizCorrect = (qIdx, oIdx) => {
        setQuizQuestions(qs => qs.map((q, i) => {
            if (i !== qIdx) return q;
            if (q.type === 'single') {
                return {
                    ...q,
                    options: q.options.map((opt, j) => ({ ...opt, isCorrect: j === oIdx }))
                };
            } else {
                return {
                    ...q,
                    options: q.options.map((opt, j) => j === oIdx ? { ...opt, isCorrect: !opt.isCorrect } : opt)
                };
            }
        }));
    };

    const handleMediaFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setMediaFile(file);
        setMediaUrlInput('');
        setMediaPreview(URL.createObjectURL(file));
    };

    const handleMediaSave = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (!lessonPage?.id) {
                alert(t('modalBlock.errors.noLessonPage'));
                return;
            }

            const isUpdate = Action === 'Update' && selectedBlock?.id;
            const blockType = type === 'video' ? 'video' : 'image';
            const width = blockType === 'video' ? 600 : 400;
            const height = blockType === 'video' ? 400 : 300;
            const payloadBase = {
                block_type: blockType,
                title: '',
                text: '',
                title_size: 24,
                text_size: 16,
                width: toInteger(selectedBlock?.width, width),
                height: toInteger(selectedBlock?.height, height),
                left_position: toInteger(selectedBlock?.left, 0),
                top_position: toInteger(selectedBlock?.top, 0),
                order: isUpdate ? (selectedBlock?.order ?? 0) : (lessonPage.blocks?.length || 0),
                page: lessonPage.id,
            };

            let savedBlock;
            if (mediaMode === 'file') {
                if (!mediaFile) {
                    alert(t('modalBlock.errors.chooseFile'));
                    return;
                }
                const formData = new FormData();
                Object.entries(payloadBase).forEach(([key, value]) => {
                    formData.append(key, String(value));
                });
                formData.append('media_file', mediaFile);
                formData.append('URL_media', '');

                savedBlock = isUpdate
                    ? await updateLessonPageBlock(selectedBlock.id, formData)
                    : await createLessonPageBlock(formData);
            } else {
                const urlValue = mediaUrlInput.trim();
                if (!urlValue) {
                    alert(t('modalBlock.errors.enterUrl'));
                    return;
                }
                savedBlock = isUpdate
                    ? await updateLessonPageBlock(selectedBlock.id, { ...payloadBase, URL_media: urlValue })
                    : await createLessonPageBlock({ ...payloadBase, URL_media: urlValue });
            }

            // Log the change
            if (onLogChange) {
                const action = isUpdate ? `Updated ${blockType} block` : `Added ${blockType} block`;
                await onLogChange(action, { 
                    block_type: blockType,
                    media_mode: mediaMode
                });
            }

            if (onModalSave) onModalSave({ block: savedBlock });
            else closeModal();

            return savedBlock;
        } catch (err) {
            alert(t('modalBlock.errors.saveFailed', { error: typeof err === 'object' ? JSON.stringify(err) : err }));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Save handler using RESTful API (create or update block, then questions, then choices)
    const handleSave = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (Action !== 'Add' && Action !== 'Update') {
                alert(t('modalBlock.errors.invalidQuizAction'));
                return;
            }
            if (onSaveQuizDraft) {
                await onSaveQuizDraft({
                    action: Action,
                    quizQuestions,
                    selectedBlock,
                    lessonPage,
                });
                
                // Log the change
                if (onLogChange) {
                    const action = Action === 'Add' ? 'Added quiz block' : 'Updated quiz block';
                    await onLogChange(action, { 
                        block_type: 'quiz',
                        question_count: quizQuestions.length
                    });
                }
                
                if (onModalSave) onModalSave({ refresh: false });
                else closeModal(false);
                return;
            }

            if (onModalSave) onModalSave();
            else closeModal();
        } catch (err) {
            alert(t('modalBlock.errors.quizSaveFailed', { error: typeof err === 'object' ? JSON.stringify(err) : err }));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (type === 'image' || type === 'video') {
        const title = type === 'video' ? t('modalBlock.video') : t('modalBlock.image');
        return (
            <>
                <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
                <div className="Modal">
                <div className="media-modal-box">
                    <h2 style={{ marginTop: 0, marginBottom: 18 }}>{Action === 'Update' ? t('modalBlock.editBlockTitle', { type: title }) : t('modalBlock.createBlockTitle', { type: title })}</h2>
                    <div className="media-mode-switch">
                        <button
                            type="button"
                            className={`media-mode-btn ${mediaMode === 'file' ? 'active' : ''}`}
                            onClick={() => setMediaMode('file')}
                        >
                            {t('modalBlock.uploadFile')}
                        </button>
                        <button
                            type="button"
                            className={`media-mode-btn ${mediaMode === 'url' ? 'active' : ''}`}
                            onClick={() => setMediaMode('url')}
                        >
                            {t('modalBlock.urlLink')}
                        </button>
                    </div>
                    <div className="media-modal-body">
                        {mediaMode === 'file' ? (
                            <>
                                <label className="media-label">{t('modalBlock.chooseFileLabel', { type: title.toLowerCase() })}</label>
                                <input
                                    type="file"
                                    accept={type === 'video' ? 'video/*' : 'image/*'}
                                    className="media-file-input"
                                    onChange={handleMediaFileChange}
                                />
                                {mediaPreview && (
                                    <div className="media-preview-wrap">
                                        {type === 'video' ? (
                                            <video src={mediaPreview} controls className="media-preview" />
                                        ) : (
                                            <img src={mediaPreview} alt="Media preview" className="media-preview" />
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <label className="media-label">{t('modalBlock.urlLabel', { type: title })}</label>
                                <input
                                    type="url"
                                    className="media-url-input"
                                    placeholder={t('modalBlock.urlPlaceholder', { type: title.toLowerCase() })}
                                    value={mediaUrlInput}
                                    onChange={(e) => setMediaUrlInput(e.target.value)}
                                />
                                {mediaUrlInput && (
                                    <div className="media-preview-wrap">
                                        {type === 'video' ? (
                                            <video src={mediaUrlInput} controls className="media-preview" />
                                        ) : (
                                            <img src={mediaUrlInput} alt="Media preview" className="media-preview" />
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button className="btn-primary" onClick={handleMediaSave} style={{ flex: 1 }} disabled={isSubmitting}>{t('modalBlock.saveMedia')}</button>
                        <button className="btn-cancel" onClick={closeModal} style={{ flex: 1, borderRadius: '12px' }}>{t('common.cancel')}</button>
                    </div>
                </div>
                </div>
            </>
        );
    }

    if (type === 'quiz') {
        return (
            <>
                <LoadingIndicator active={isSubmitting} label={t('common.loading')} />
                <div className="Modal">
                <div className="quiz-modal-box" style={{ width: 700 }}>
                    <h2 style={{ marginTop: 0, marginBottom: 24 }}>{t('modalBlock.createEditQuiz')}</h2>
                    <div className="quiz-builder-content">
                        {quizQuestions.map((q, qIdx) => (
                            <div className="q-builder-item" key={qIdx}>
                                <div className="q-builder-header">
                                    <span style={{ fontWeight: 700 }}>{t('modalBlock.question', { num: qIdx + 1 })}</span>
                                    <button className="tiny-btn" onClick={() => removeQuizQuestion(qIdx)} style={{ color: 'red' }}>🗑</button>
                                </div>
                                <div style={{ marginBottom: 10, display: 'flex', gap: 10 }}>
                                    <input
                                        className="input-heading"
                                        style={{ padding: 8, fontSize: '1rem' }}
                                        value={q.text}
                                        placeholder={t('modalBlock.questionPlaceholder')}
                                        onChange={e => updateQuizQuestionText(qIdx, e.target.value)}
                                    />
                                    <select
                                        className="q-type-select"
                                        value={q.type}
                                        onChange={e => updateQuizType(qIdx, e.target.value)}
                                    >
                                        <option value="single">{t('modalBlock.singleChoice')}</option>
                                        <option value="multiple">{t('modalBlock.multipleChoice')}</option>
                                    </select>
                                </div>
                                <div>
                                    {q.options.map((opt, oIdx) => {
                                        const checkType = q.type === 'single' ? 'radio' : 'checkbox';
                                        return (
                                            <div className="q-opt-row" key={oIdx}>
                                                <input
                                                    type={checkType}
                                                    name={`q-${qIdx}`}
                                                    className="q-check"
                                                    checked={!!opt.isCorrect}
                                                    onChange={() => toggleQuizCorrect(qIdx, oIdx)}
                                                />
                                                <input
                                                    className="q-input"
                                                    value={opt.text}
                                                    placeholder={t('modalBlock.answerPlaceholder')}
                                                    onChange={e => updateQuizOptionText(qIdx, oIdx, e.target.value)}
                                                    style={{ background: opt.isCorrect ? '#dcfce7' : 'white' }}
                                                />
                                                <button className="tiny-btn" onClick={() => removeQuizOption(qIdx, oIdx)} disabled={q.options.length <= 2}>×</button>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="q-add-opt" onClick={() => addQuizOption(qIdx)} style={{ cursor: 'pointer', color: '#2563eb', marginTop: 8 }}>{t('modalBlock.addOption')}</div>
                            </div>
                        ))}
                        <button className="q-btn-add" onClick={addQuizQuestion}>{t('modalBlock.addQuestion')}</button>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button className="btn-primary" onClick={handleSave} style={{ flex: 1 }} disabled={isSubmitting}>{t('modalBlock.saveQuiz')}</button>
                        <button className="btn-cancel" onClick={closeModal} style={{ flex: 1, borderRadius: '12px' }}>{t('common.cancel')}</button>
                    </div>
                </div>
                </div>
            </>
        );
    }
    // Default: text block modal
    if (type === 'card') {
        return (
            <div className={"Modal"}>
                <div className="chapter-modal-box">
                    <h2>{t('modalBlock.editCardBlock')}</h2>
                    <div className="control-row">
                        <div style={{ flex: 1 }}>
                            <span className="size-label">{t('modalBlock.headingTextLabel')}</span>
                            <input
                                className="input-heading"
                                placeholder={t('modalBlock.headingPlaceholder')}
                                value={modalHeading}
                                onChange={(e) => setModalHeading(e.target.value)}
                            />
                        </div>
                        <div className="wrapper-col">
                            <span className="size-label">{t('modalBlock.sizeRec24')}</span>
                            <input
                                type="number"
                                className="input-number"
                                placeholder="24"
                                value={modalHeadingSize}
                                onChange={(e) => setModalHeadingSize(parseInt(e.target.value) || 24)}
                            />
                        </div>
                    </div>
                    <span className="size-label">{t('modalBlock.bodyTextLabel')}</span>
                    <textarea
                        className="input-body"
                        placeholder={t('modalBlock.bodyPlaceholder')}
                        value={modalBody}
                        onChange={(e) => setModalBody(e.target.value)}
                    />
                    <div className="control-row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                        <div className="wrapper-col">
                            <span className="size-label">{t('modalBlock.bodySizeRec16')}</span>
                            <input
                                type="number"
                                className="input-number"
                                placeholder="16"
                                value={modalBodySize}
                                onChange={(e) => setModalBodySize(parseInt(e.target.value) || 16)}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button className="btn-primary" onClick={saveContent} style={{ flex: 1 }}>{t('modalBlock.saveBlock')}</button>
                        <button className="btn-cancel" onClick={closeModal} style={{ flex: 1, borderRadius: '12px' }}>{t('common.cancel')}</button>
                    </div>
                </div>
            </div>
        );
    }
}

export default ModalBlock;
